import React, { useEffect, useMemo, useRef, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { useParams } from "react-router-dom";
import {
  Input,
  Label,
  Form,
  Card,
  CardBody,
  CardHeader,
  Container,
  Button,
  Spinner,
  Alert,
} from "reactstrap";
import { useFormik } from "formik";
import schema from "./schema";
import { toast } from "react-toastify";
import {
  getRolePermission,
  updateRolePermissions,
} from "features/RolesAndPermissions/services/RoleService";
import { getRbacCatalog } from "features/Meta/services/MetaService";
import type { RbacCatalogPermissionFormModule } from "features/RolesAndPermissions/types/rbac-catalog.types";
import type {
  RolePermission,
  RolePermissionPolicyRow,
} from "features/RolesAndPermissions/types/role.types";
import { RoleCode } from "features/RolesAndPermissions/constants/role.constants";
import {
  POLICY_CONDITION_KEY_LABELS,
  POLICY_KEYS_FOR_ROLE_FORM,
  PolicyConditionKey,
  PolicyEffect,
  policyCodeGrantsUnrestrictedAccess,
} from "features/RolesAndPermissions/constants/policy.constants";
import { BackButton } from "components/common/Buttons";
import { getDocumentTitle } from "utils/helpers";

function policyKeysFromApiPermission(
  p: RolePermission["assignedPermissions"][number],
): string[] {
  const raw = p.policies ?? [];
  return raw
    .map((x) => {
      if (x && typeof x === "object" && "conditionKey" in x && x.conditionKey) {
        return String(x.conditionKey);
      }
      const snake = (x as { condition_key?: string }).condition_key;
      return snake ? String(snake) : "";
    })
    .filter(Boolean);
}

function buildPolicyMapFromRole(
  permissions: RolePermission["assignedPermissions"] | undefined,
): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  permissions?.forEach((p) => {
    map[p.code] = policyKeysFromApiPermission(p);
  });
  return map;
}

function policyRowsFromConditionKeys(
  keys: string[],
): RolePermissionPolicyRow[] {
  return keys.map((conditionKey, index) => ({
    effect: PolicyEffect.ALLOW,
    conditionKey,
    priority: index,
    isActive: true,
  }));
}

function ModuleCheckbox({
  checked,
  indeterminate,
  onChange,
  disabled,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="form-check-input flex-shrink-0"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

const SetPermissionForm = () => {
  document.title = getDocumentTitle("Set Role Permissions");

  const { id } = useParams();
  const [record, setRecord] = useState<RolePermission | null>(null);
  const [loading, setLoading] = useState(false);
  const [recordLoading, setRecordLoading] = useState(true);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [permissionFormConfig, setPermissionFormConfig] = useState<
    RbacCatalogPermissionFormModule[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");

  const initialPolicyByPermission = useMemo(
    () => buildPolicyMapFromRole(record?.assignedPermissions),
    [record],
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      permissions: record?.assignedPermissions?.map((item) => item.code) ?? [],
      policyByPermission: initialPolicyByPermission,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const roleId = Number(id);

        // Build a lookup of which permission codes support row-level policies
        // and what the valid policy keys are, from the catalog.
        const validPolicyKeySet = new Set<string>(POLICY_KEYS_FOR_ROLE_FORM);
        const supportsRowPolicies = new Set<string>(
          permissionFormConfig.flatMap((mod) =>
            mod.actions
              .filter((a) => a.supportsRowPolicies)
              .map((a) => a.value),
          ),
        );

        const payload = {
          roleId,
          permissions: values.permissions.map((code) => ({
            code,
            policies: supportsRowPolicies.has(code)
              ? policyRowsFromConditionKeys(
                  (values.policyByPermission[code] ?? []).filter((k) =>
                    validPolicyKeySet.has(k),
                  ),
                )
              : [],
          })),
        };
        const response = await updateRolePermissions(roleId, payload);
        if (response.success) {
          toast.success(response.message);
        }
      } catch (err) {
        toast.error((err as any).response?.data?.message ?? "Save failed");
      } finally {
        setLoading(false);
      }
    },
  });

  const togglePolicyKey = (permissionCode: string, key: string) => {
    const map = { ...validation.values.policyByPermission };
    const current = map[permissionCode] ?? [];
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    map[permissionCode] = next;
    validation.setFieldValue("policyByPermission", map);
  };

  const handlePermissionToggle = (
    permissionCode: string,
    supportsRowPolicies: boolean,
  ) => {
    const current = validation.values.permissions;
    const map = { ...validation.values.policyByPermission };

    if (current.includes(permissionCode)) {
      validation.setFieldValue(
        "permissions",
        current.filter((p) => p !== permissionCode),
      );
      delete map[permissionCode];
      validation.setFieldValue("policyByPermission", map);
    } else {
      validation.setFieldValue("permissions", [...current, permissionCode]);
      if (supportsRowPolicies) {
        const existing = map[permissionCode];
        if (!existing?.length) {
          map[permissionCode] = [PolicyConditionKey.ALL];
        }
      }
      validation.setFieldValue("policyByPermission", map);
    }
  };

  const handleModuleToggle = (
    item: RbacCatalogPermissionFormModule,
    checked: boolean,
  ) => {
    const actionValues = item.actions.map((a) => a.value);
    const current = validation.values.permissions;
    const map = { ...validation.values.policyByPermission };

    if (checked) {
      const merged = Array.from(new Set([...current, ...actionValues]));
      item.actions.forEach((a) => {
        if (!map[a.value]?.length && a.supportsRowPolicies) {
          map[a.value] = [PolicyConditionKey.ALL];
        }
      });
      validation.setFieldValue("permissions", merged);
      validation.setFieldValue("policyByPermission", map);
    } else {
      validation.setFieldValue(
        "permissions",
        current.filter((p) => !actionValues.includes(p)),
      );
      actionValues.forEach((code) => {
        delete map[code];
      });
      validation.setFieldValue("policyByPermission", map);
    }
  };

  useEffect(() => {
    const fetchRecord = async () => {
      setRecordLoading(true);
      setRecordError(null);
      try {
        const response = await getRolePermission(Number(id));
        if (response.success) {
          setRecord(response.data);
          console.log(response.data);
        } else {
          setRecordError(response.message?.trim() || "Could not load role.");
        }
      } catch (err) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Could not load role.";
        setRecordError(msg);
      } finally {
        setRecordLoading(false);
      }
    };

    void fetchRecord();
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const loadCatalog = async () => {
      setCatalogLoading(true);
      setCatalogError(null);
      try {
        const res = await getRbacCatalog();
        if (cancelled) return;
        if (res.success && res.data?.permissionFormConfig?.length) {
          setPermissionFormConfig(res.data.permissionFormConfig);
        } else {
          setCatalogError(
            res.message?.trim() ||
              "RBAC catalog did not return any permissions. Check GET /meta/rbac-catalog.",
          );
          setPermissionFormConfig([]);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Could not load permission catalog.";
        setCatalogError(msg);
        setPermissionFormConfig([]);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    };
    void loadCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  const isSuperAdmin = record?.role.code === RoleCode.SUPER_ADMIN;
  const selectedCount = validation.values.permissions.length;

  const filteredPermissionFormConfig = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return permissionFormConfig;
    return permissionFormConfig
      .map((module) => {
        const moduleMatches =
          module.label.toLowerCase().includes(q) ||
          module.module.toLowerCase().includes(q);
        if (moduleMatches) return module;
        const filteredActions = module.actions.filter(
          (action) =>
            action.label.toLowerCase().includes(q) ||
            action.value.toLowerCase().includes(q),
        );
        return { ...module, actions: filteredActions };
      })
      .filter((module) => module.actions.length > 0);
  }, [permissionFormConfig, searchQuery]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={
              record ? `Permissions: ${record.role.name}` : "Set Permissions"
            }
            pageTitle="User Management"
            backTo={{
              label: "Roles & Permissions",
              path: "/user-management/roles-and-permissions",
            }}
          />

          <Form
            className="needs-validation"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <Card>
              <CardHeader>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="avatar-sm flex-shrink-0 bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center">
                    <i className="ri-shield-check-line fs-18" />
                  </span>
                  <div className="flex-grow-1">
                    <h5 className="card-title mb-0">
                      {record?.role.name ?? "—"}
                    </h5>
                    <p className="text-muted small mb-0 mt-1">
                      Choose what this role can do. For list actions, set which
                      records apply.
                    </p>
                  </div>
                  {!isSuperAdmin && (
                    <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2">
                      {selectedCount} permission
                      {selectedCount !== 1 ? "s" : ""} selected
                    </span>
                  )}
                </div>
              </CardHeader>

              <CardBody>
                {isSuperAdmin && (
                  <Alert color="info" className="mb-4">
                    <i className="ri-information-line me-2" />
                    <strong>Super Admin</strong> has full access. Permissions
                    cannot be changed.
                  </Alert>
                )}

                {recordError && (
                  <Alert color="danger" className="mb-4">
                    <i className="ri-error-warning-line me-2" />
                    {recordError}
                  </Alert>
                )}

                {catalogError && (
                  <Alert color="danger" className="mb-4">
                    <i className="ri-error-warning-line me-2" />
                    {catalogError}
                  </Alert>
                )}

                {catalogLoading || recordLoading ? (
                  <div className="text-center py-5 text-muted">
                    <Spinner className="me-2" />
                    {catalogLoading
                      ? "Loading permission catalog…"
                      : "Loading role…"}
                  </div>
                ) : (
                  <>
                    <div className="position-relative mb-4">
                      <Input
                        type="search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search module or access..."
                        className="ps-5"
                        disabled={
                          catalogLoading || !permissionFormConfig.length
                        }
                      />
                      <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                    </div>

                    {filteredPermissionFormConfig.length === 0 ? (
                      <div className="text-center text-muted py-5">
                        No permissions found for &ldquo;
                        {searchQuery.trim()}&rdquo;.
                      </div>
                    ) : (
                      <div className="row g-3">
                        {filteredPermissionFormConfig.map((item) => {
                          const moduleActionValues = item.actions.map(
                            (a) => a.value,
                          );
                          const selectedInModule = moduleActionValues.filter(
                            (v) => validation.values.permissions.includes(v),
                          ).length;
                          const totalInModule = moduleActionValues.length;
                          const allChecked =
                            selectedInModule === totalInModule &&
                            totalInModule > 0;
                          const someChecked =
                            selectedInModule > 0 && !allChecked;

                          return (
                            <div key={item.module} className="col-md-6">
                              <Card className="h-100 border shadow-none mb-0">
                                <CardHeader className="py-2 px-3 bg-light d-flex align-items-center gap-2">
                                  {!isSuperAdmin && (
                                    <ModuleCheckbox
                                      checked={allChecked}
                                      indeterminate={someChecked}
                                      onChange={(e) =>
                                        handleModuleToggle(
                                          item,
                                          e.target.checked,
                                        )
                                      }
                                    />
                                  )}
                                  <span className="fw-semibold text-dark flex-grow-1 small">
                                    {item.label}
                                  </span>
                                  <span
                                    className={`badge rounded-pill ${
                                      selectedInModule > 0
                                        ? "bg-primary"
                                        : "bg-light text-muted border"
                                    }`}
                                  >
                                    {selectedInModule}/{totalInModule}
                                  </span>
                                </CardHeader>

                                <CardBody className="p-2">
                                  {totalInModule === 0 ? (
                                    <p className="text-muted small mb-0 p-2">
                                      No actions defined.
                                    </p>
                                  ) : (
                                    <div className="d-flex flex-column gap-1">
                                      {item.actions.map((a) => {
                                        const checked =
                                          validation.values.permissions.includes(
                                            a.value,
                                          );
                                        const keys =
                                          validation.values.policyByPermission[
                                            a.value
                                          ] ?? [];
                                        const hasWarning =
                                          checked &&
                                          keys.some((k) =>
                                            policyCodeGrantsUnrestrictedAccess(
                                              k,
                                            ),
                                          );

                                        return (
                                          <div
                                            key={a.value}
                                            className={`p-2 rounded-2 ${
                                              checked
                                                ? "bg-primary-subtle"
                                                : "bg-light"
                                            }`}
                                          >
                                            <div className="form-check mb-0">
                                              <Input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`${a.value}-input`}
                                                onChange={() =>
                                                  handlePermissionToggle(
                                                    a.value,
                                                    a.supportsRowPolicies,
                                                  )
                                                }
                                                checked={checked}
                                                disabled={isSuperAdmin}
                                              />
                                              <Label
                                                className="form-check-label fw-medium"
                                                htmlFor={`${a.value}-input`}
                                              >
                                                {a.label}
                                              </Label>
                                            </div>

                                            {checked &&
                                              a.supportsRowPolicies && (
                                                <div className="ms-4 mt-1 d-flex flex-wrap align-items-center gap-1">
                                                  <span className="text-muted small me-1">
                                                    Scope:
                                                  </span>
                                                  {POLICY_KEYS_FOR_ROLE_FORM.map(
                                                    (pk) => {
                                                      const on = keys.includes(
                                                        pk as string,
                                                      );
                                                      return (
                                                        <button
                                                          key={pk}
                                                          type="button"
                                                          className={`btn btn-sm px-2 py-0 rounded-pill border ${
                                                            on
                                                              ? "btn-primary"
                                                              : "btn-light text-muted"
                                                          }`}
                                                          style={{
                                                            fontSize: "0.7rem",
                                                          }}
                                                          onClick={() =>
                                                            togglePolicyKey(
                                                              a.value,
                                                              pk as string,
                                                            )
                                                          }
                                                          disabled={
                                                            isSuperAdmin
                                                          }
                                                        >
                                                          {
                                                            POLICY_CONDITION_KEY_LABELS[
                                                              pk
                                                            ]
                                                          }
                                                        </button>
                                                      );
                                                    },
                                                  )}
                                                  {hasWarning && (
                                                    <span className="text-warning small ms-1">
                                                      <i className="ri-alert-line me-1" />
                                                      Unrestricted access
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </CardBody>
                              </Card>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>

            <div className="position-sticky bottom-0 z-3 py-3 mt-3 mb-2 border-0 shadow-none bg-body">
              <div className="d-flex flex-wrap justify-content-end gap-2">
                <BackButton />
                {!loading ? (
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      isSuperAdmin ||
                      catalogLoading ||
                      recordLoading ||
                      !!catalogError ||
                      !!recordError ||
                      !permissionFormConfig.length
                    }
                  >
                    <i className="ri-save-line align-bottom me-1" />
                    Save changes
                  </button>
                ) : (
                  <Button color="primary" disabled>
                    <Spinner size="sm" className="me-2" />
                    Saving…
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SetPermissionForm;
